import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { MapRef } from 'react-map-gl';
import type { SurveyAreaCandidate } from 'components/query-map/map-pick-control';
import type { MapWorkspaceState } from 'components/query-map/map-workspace-state';
import { EDGE_HOVER_EVENT } from 'components/query-map/draw-polygons-control';
import {
  surveyPickEnabledAtom,
  surveyPickRefAtom,
  unitClickEnabledAtom,
  unitClickRefAtom,
  workspaceEditingIndexAtom,
  workspaceIsDrawingAtom,
  workspaceNeedsMapAtom,
  workspacePickedSurveyAreasAtom,
  workspacePolygonsAtom,
  workspacePreviewColorAtom,
  workspacePreviewGeometryAtom,
  workspaceSnapToGridAtom,
} from './atoms';
import type {
  Handler,
  HandlerRef,
  MapWorkspaceStateHandle,
  ScreenPoint,
} from './types';

/**
 * A handler slot backed by a ref atom: the setter drops a handler in, and the map gets
 * a stable invoker while one is registered, or null so its clicks stay inert.
 *
 * @param {object} refAtom - The atom holding the handler's ref.
 * @param {object} enabledAtom - Whether a handler is registered.
 * @return {Array} The invoker and the setter.
 */
const useAtomHandlerSlot = <T>(
  refAtom: Atom<HandlerRef<T>>,
  enabledAtom: typeof unitClickEnabledAtom
): [Handler<T>, (handler: Handler<T>) => void] => {
  const ref = useAtomValue(refAtom);
  const [enabled, setEnabled] = useAtom(enabledAtom);

  const setHandler = useCallback(
    (handler: Handler<T>) => {
      ref.current = handler;
      setEnabled(!!handler);
    },
    [ref, setEnabled]
  );

  const invoke = useCallback((arg: T) => ref.current?.(arg), [ref]);

  return [enabled ? invoke : null, setHandler];
};

/**
 * The form-to-map contract both hosts of the Create New Area panel share. Every caller
 * reads and writes the same state, so the panel and the map can sit anywhere on the
 * page. The owning page pairs it with {@link useResetMapWorkspaceOnUnmount}.
 *
 * @return {MapWorkspaceStateHandle} The contract and its resets.
 */
export const useMapWorkspaceState = (): MapWorkspaceStateHandle => {
  const [polygons, setPolygons] = useAtom(workspacePolygonsAtom);
  const [isDrawing, setIsDrawing] = useAtom(workspaceIsDrawingAtom);
  const [needsMap, setNeedsMap] = useAtom(workspaceNeedsMapAtom);
  const [editingIndex, setEditingIndex] = useAtom(workspaceEditingIndexAtom);
  const [snapToGrid, setSnapToGrid] = useAtom(workspaceSnapToGridAtom);
  const [previewGeometry, setPreviewGeometry] = useAtom(
    workspacePreviewGeometryAtom
  );
  const [previewColor, setPreviewColor] = useAtom(workspacePreviewColorAtom);
  const [pickedSurveyAreas, setPickedSurveyAreas] = useAtom(
    workspacePickedSurveyAreasAtom
  );
  const [onUnitClick, onUnitClickChange] = useAtomHandlerSlot<number>(
    unitClickRefAtom,
    unitClickEnabledAtom
  );
  const [onSurveyAreaPick, onSurveyAreaPickChange] = useAtomHandlerSlot<
    SurveyAreaCandidate[]
  >(surveyPickRefAtom, surveyPickEnabledAtom);
  const resetSnap = useSetAtom(workspaceSnapToGridAtom);

  const drawing = useMemo<MapWorkspaceState>(
    () => ({
      polygons,
      onPolygonsChange: setPolygons,
      isDrawing,
      onIsDrawingChange: setIsDrawing,
      needsMap,
      onNeedsMapChange: setNeedsMap,
      editingIndex,
      onEditingIndexChange: setEditingIndex,
      snapToGrid,
      onSnapToGridChange: setSnapToGrid,
      previewGeometry,
      onPreviewGeometryChange: setPreviewGeometry,
      previewColor,
      onPreviewColorChange: setPreviewColor,
      onUnitClick,
      onUnitClickChange,
      onSurveyAreaPick,
      onSurveyAreaPickChange,
      pickedSurveyAreas,
      onPickedSurveyAreasChange: setPickedSurveyAreas,
    }),
    [
      polygons,
      setPolygons,
      isDrawing,
      setIsDrawing,
      needsMap,
      setNeedsMap,
      editingIndex,
      setEditingIndex,
      snapToGrid,
      setSnapToGrid,
      previewGeometry,
      setPreviewGeometry,
      previewColor,
      setPreviewColor,
      onUnitClick,
      onUnitClickChange,
      onSurveyAreaPick,
      onSurveyAreaPickChange,
      pickedSurveyAreas,
      setPickedSurveyAreas,
    ]
  );

  // The snap preference outlives a closed panel, so `reset` leaves it alone.
  const reset = useCallback(() => {
    setPolygons([]);
    setIsDrawing(false);
    setNeedsMap(false);
    setEditingIndex(null);
    setPreviewGeometry(null);
    setPreviewColor(null);
    onUnitClickChange(null);
    onSurveyAreaPickChange(null);
    setPickedSurveyAreas([]);
  }, [
    setPolygons,
    setIsDrawing,
    setNeedsMap,
    setEditingIndex,
    setPreviewGeometry,
    setPreviewColor,
    onUnitClickChange,
    onSurveyAreaPickChange,
    setPickedSurveyAreas,
  ]);

  const resetAll = useCallback(() => {
    reset();
    resetSnap(true);
  }, [reset, resetSnap]);

  return { drawing, reset, resetAll };
};

/**
 * Drops the shared state when the page that owns the panel unmounts, the snap
 * preference included, as page-local state did. Called once, by that page only:
 * other readers (a map that can be toggled off) must not reset the panel's work.
 *
 * @param {Function} resetAll - `resetAll` from {@link useMapWorkspaceState}.
 */
export const useResetMapWorkspaceOnUnmount = (resetAll: () => void): void => {
  useEffect(() => () => resetAll(), [resetAll]);
};

/**
 * The point on the edited polygon's boundary the pointer is near, for the "click to
 * add a vertex" tip — the unit builder's affordance. The edit mode fires the point
 * (deduped) and null as the pointer leaves the line. The host reads it too, to swap
 * its cursor while the tip is up.
 *
 * @param {MapRef | null} mapRef - The map the edit runs on.
 * @return {ScreenPoint | null} Where the tip goes, or null when it is down.
 */
export const useAddVertexPoint = (
  mapRef: MapRef | null
): ScreenPoint | null => {
  const [point, setPoint] = useState<ScreenPoint | null>(null);

  useEffect(() => {
    if (!mapRef) {
      return undefined;
    }
    /**
     * Follows the edge the pointer is over.
     *
     * @param {object} event - The edit mode's edge-hover event.
     */
    const handleEdgeHover = (event: { point?: ScreenPoint | null }) =>
      setPoint(event.point ?? null);
    mapRef.on(EDGE_HOVER_EVENT, handleEdgeHover);
    return () => {
      mapRef.off(EDGE_HOVER_EVENT, handleEdgeHover);
    };
  }, [mapRef]);

  return point;
};
